import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react'; // ADD THESE IMPORTS

const FormSuccess = ({ onReset }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Get data passed from the form submission
  const { submissionId, customerName, formData } = location.state || {};

  // Debug log to see what we're receiving
  useEffect(() => {
    console.log('🎉 Success page loaded with:', { 
      submissionId, 
      customerName, 
      hasFormData: !!formData,
      locationState: location.state 
    });
  }, [location.state]);

  const handleHome = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full animate-fade-in">
        {/* Animated Success Card */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-green-200 dark:border-green-800 p-8 text-center transform transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
          
          {/* Animated Success Icon */}
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-lg animate-bounce-in">
              <span className="text-4xl text-white">✓</span>
            </div>
            {/* Floating particles */}
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-ping"></div>
            <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-400 rounded-full animate-ping animation-delay-1000"></div>
          </div>
          
          {/* Success Message */}
          <div className="space-y-4 mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-700 bg-clip-text text-transparent dark:from-green-400 dark:to-emerald-300">
              Amazing
            </h1>
            
            <div className="space-y-2">
              <p className="text-xl text-gray-700 dark:text-gray-300 font-medium">
                Your project is in great hands 🛠️
              </p>
              
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                We've received the Lead form already for reviewing your requirements.
              </p>
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 mb-8 border border-blue-200 dark:border-blue-800">
            <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center justify-center gap-2 text-lg">
              <span>⏱️</span>
              What's Next?
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                <span className="text-blue-800 dark:text-blue-200 text-sm font-medium">Review & Analysis</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
                <span className="text-blue-800 dark:text-blue-200 text-sm font-medium">Download forms</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <button 
              onClick={handleHome}
              disabled={isNavigating}
              className="group bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isNavigating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Redirecting...
                </>
              ) : (
                <>
                  <span>🚀</span>
                  HomePage
                </>
              )}
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="mt-8 text-center">
          <div className="flex justify-center space-x-2 opacity-50">
            {['🔨', '🏡', '🌿', '✨', '🎯'].map((emoji, index) => (
              <span 
                key={index}
                className="text-2xl animate-float"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {emoji}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Add some custom styles for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-bounce-in {
          animation: bounce-in 0.8s ease-out;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .hover\\:shadow-3xl:hover {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
};

export default FormSuccess;